import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Search, MapPin, X, Filter, Calendar, Megaphone, Users } from "lucide-react";
import { IKO_KEYS, IKO, IkoMix, RoseRow, LovableTabs } from "@/design-system";
import { useAuth } from "@/components/AuthProvider";
import { useIsDesktop } from "@/lib/useMediaQuery";
import { useChains, DiscoverChainCard } from "@/components/member/ChainCards";
import { VerticalCard, PosterViewer, useWidthBand,
  IkoChipRow, metaLine, ikoTextOnNight, ikoLabel, bucketEvent, nextStartAt, ikoTitleVar,
} from "./feedCard";

const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api` });

/**
 * Vania Msg 215 §2/§3 — Discover with 4-pill segmented bar + filter sheet.
 *   events | venues | organisers | watu
 * Filters live behind a funnel icon that opens a bottom sheet (mobile) or
 * side panel (wide). Active filters show as removable chips under the bar.
 */
const AREAS = ["Westlands", "Kilimani", "Kileleshwa", "CBD", "Karen", "Langata",
               "Lavington", "Kasarani", "Thika Road", "Eastlands"];

function dayBand(iso, now = new Date()) {
  // Vania Msg 217 §3 — buckets are: this weekend, this week, next week, then
  // NAMED MONTHS (never a "later this year" that goes stale at year-end).
  // Each event lands in the FIRST bucket it matches — never in two.
  if (!iso) return null;
  const d = new Date(iso);
  if (d < now) return null;
  const startOfDay = (dt) => { const x = new Date(dt); x.setHours(0,0,0,0); return x; };
  const today = startOfDay(now);
  const inN = (n) => new Date(today.getTime() + n * 864e5);
  const dow = today.getDay(); // 0=Sun
  const daysToSat = (6 - dow + 7) % 7;
  const daysToSun = (7 - dow) % 7;
  const thisWeekend = new Set([inN(daysToSat).toDateString(), inN(daysToSun).toDateString()]);
  const nextSun = inN(daysToSun);
  const followingMon = inN(daysToSun + 1);
  const followingSun = inN(daysToSun + 7);
  const key = d.toDateString();
  if (thisWeekend.has(key)) return "weekend";
  if (d >= today && d <= nextSun) return "week";
  if (d >= followingMon && d <= followingSun) return "next_week";
  // Beyond next week → named month key. e.g. "month:2026-10"
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `month:${y}-${m}`;
}
const MONTH_NAMES = ["january","february","march","april","may","june","july","august","september","october","november","december"];
function monthLabel(monthKey) {
  const [y, m] = monthKey.slice("month:".length).split("-");
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}
const BUDGETS = [
  { key: "any", label: "any" }, { key: "free", label: "free" },
  { key: "u1k", label: "under 1k" }, { key: "1k3k", label: "1k to 3k" }, { key: "3kp", label: "3k+" },
];
function priceBand(priceRaw) {
  const p = (priceRaw || "").toLowerCase();
  if (!p || p === "free") return "free";
  const m = p.match(/(\d[\d,]*)/);
  const n = m ? parseInt(m[1].replace(/,/g, ""), 10) : 0;
  if (n < 1000) return "u1k";
  if (n <= 3000) return "1k3k";
  return "3kp";
}

const PILLS = [
  { k: "events",     label: "events", Icon: Calendar },
  { k: "venues",     label: "venues", Icon: MapPin },
  { k: "organisers", label: "curators", Icon: Megaphone },
  { k: "watu",       label: "watu", Icon: Users },
];
// WW-146.g · around nairobi = the city and 11 towns (D59)
const HOODS = ["Westlands", "Kilimani", "Kileleshwa", "CBD", "Karen", "Langata", "Lavington", "Kasarani", "Thika Road", "Eastlands"];
const TOWNS = ["Limuru", "Tigoni", "Kiambu Town", "Ruiru", "Thika", "Kitengela", "Ngong", "Rongai", "Syokimau", "Athi River", "Mlolongo"];
const REGIONS = [
  { k: "nairobi", label: "around nairobi", line: "the city and 11 towns" },
  { k: "outoftown", label: "out of town", line: "rest of kenya" },
  { k: "safari", label: "safari", line: "outside kenya" },
];
const FILTER_TABS = ["iko", "area", "day", "price"];

export default function DiscoverPage() {
  const isDesktop = useIsDesktop();
  const [events, setEvents] = useState([]);
  const [venuesList, setVenuesList] = useState([]);
  const [organisers, setOrganisers] = useState([]);
  const [people, setPeople] = useState([]);
  // Msg 234 blocker 2 · groups of outside-Nairobi events by town.
  const [outsideGroups, setOutsideGroups] = useState([]);
  const [q, setQ] = useState("");
  // Wave 10C + Wave 16.4 (Vania 2 Oct) · log every committed search string
  // AND the result count. Debounced to one event per committed query.
  const lastLoggedRef = useRef("");
  // `totalResults` has to be recomputed inside the effect because the memos
  // below depend on `q`; capturing them via closure here keeps the dependency
  // list honest.
  useEffect(() => {
    const trimmed = (q || "").trim().toLowerCase();
    if (!trimmed || trimmed.length < 2) return;
    const t = setTimeout(() => {
      if (lastLoggedRef.current === trimmed) return;
      lastLoggedRef.current = trimmed;
      // compute total matches across the three result lanes at log time.
      const total = (searchedEventsRef.current?.length || 0)
                   + (searchedVenuesRef.current?.length || 0)
                   + (searchedOrgsRef.current?.length || 0);
      import("@/lib/analytics").then((m) => m.trackSearch(trimmed, total));
    }, 900);
    return () => clearTimeout(t);
  }, [q]);
  // Ref mirrors of the three result memos so the search tracker can read the
  // latest counts without re-firing on every tick.
  const searchedEventsRef = useRef([]);
  const searchedVenuesRef = useRef([]);
  const searchedOrgsRef = useRef([]);
  const [pill, setPill] = useState("events");
  const [viewer, setViewer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Wave 15.11 (Vania 4 Oct) · scroll-edge fade only shows after the member
  // has actually scrolled. At rest the first card sits 16px below the sticky
  // bar with no gradient overlapping it.
  const scrollRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setScrolled(el.scrollTop > 0);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const [iko, setIko] = useState("all");
  const [day, setDay] = useState("any");
  const [dayPick, setDayPick] = useState("");
  const [budget, setBudget] = useState("any");
  const [area, setArea] = useState("any");
  const [venueIko, setVenueIko] = useState("all");
  const [venueArea, setVenueArea] = useState("any");
  // WW-146.g · the place lives in the filter drawer's area tab: around nairobi, out of town or safari.
  const [loc, setLocState] = useState(() => {
    try { return localStorage.getItem("ww.discover.region") || "nairobi"; } catch { return "nairobi"; }
  });
  const setLoc = (v) => { setLocState(v); try { localStorage.setItem("ww.discover.region", v); } catch { /* private mode */ } };
  const [safariEvents, setSafariEvents] = useState([]);
  const routeLoc = useLocation();
  useEffect(() => {
    const v = new URLSearchParams(routeLoc.search).get("q");
    if (v !== null) { setQ(v); }
  }, [routeLoc.search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, vn, og] = await Promise.all([
        client.get("/public/events"),
        client.get("/public/venues?with_counts=true"),
        client.get("/public/organisers?with_counts=true"),
      ]);
      setEvents(ev.data.events || []);
      setVenuesList(vn.data.venues || []);
      setOrganisers(og.data.organisers || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    client.get("/public/events/outside-nairobi").then((r) => setOutsideGroups(r.data.groups || []))
      .catch(() => setOutsideGroups([]));
    client.get("/public/events/safari").then((r) => setSafariEvents(r.data.events || [])).catch(() => setSafariEvents([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (pill === "watu") {
        try {
          const r = await client.get(`/people/search?q=${encodeURIComponent(q || "a")}`);
          setPeople(r.data.people || []);
        } catch { setPeople([]); }
      }
    }, 200);
    return () => clearTimeout(t);
  }, [pill, q]);

  const qLower = q.trim().toLowerCase();
  const match = (h) => !qLower || (h || "").toLowerCase().includes(qLower);
  const searchedEvents = useMemo(() => events.filter((e) => match(e.title) || match(e.venue_display) || match(e.organiser_handle)), [events, qLower]);
  const searchedVenues = useMemo(() => venuesList.filter((v) => match(v.name) || match(v.area)), [venuesList, qLower]);
  const searchedOrgs = useMemo(() => organisers.filter((o) => match(o.handle) || match(o.name)), [organisers, qLower]);
  // Wave 16.4 · mirror search result counts into refs so the tracker effect
  // above can log an accurate `result_count` for every query.
  useEffect(() => { searchedEventsRef.current = searchedEvents; }, [searchedEvents]);
  useEffect(() => { searchedVenuesRef.current = searchedVenues; }, [searchedVenues]);
  useEffect(() => { searchedOrgsRef.current = searchedOrgs; }, [searchedOrgs]);
  const filteredForRails = useMemo(() => {
    const now = new Date();
    return searchedEvents.filter((e) => {
      // WW-146 item 4 · around nairobi leaves out-of-town rows to the out of town radio (search still finds them).
      if (!qLower && e.out_of_town) return false;
      if (iko !== "all" && e.iko !== iko) return false;
      if (budget !== "any" && priceBand(e.price) !== budget) return false;
      if (area !== "any" && !(e.venue_display || "").toLowerCase().includes(area.toLowerCase())) return false;
      if (day === "today") { if (bucketEvent(nextStartAt(e), now, e.end_at) !== "today") return false; }
      else if (day === "tomorrow") {
        const tom = new Date(now.getTime() + 864e5).toDateString();
        if (new Date(nextStartAt(e) || 0).toDateString() !== tom) return false;
      } else if (day === "weekend" || day === "week" || day === "next_week") {
        // Msg 234 §9 · match the same buckets used by rail headers.
        if (dayBand(nextStartAt(e), now) !== day) return false;
      } else if (day === "pick" && dayPick && (nextStartAt(e) || "").slice(0, 10) !== dayPick) return false;
      return true;
    });
  }, [searchedEvents, iko, budget, area, day, dayPick, qLower]);
  const filteredVenues = useMemo(() => searchedVenues.filter((v) => {
    if (venueArea !== "any" && !(v.area || "").toLowerCase().includes(venueArea.toLowerCase())) return false;
    if (venueIko !== "all") {
      const c = v.iko_mix?.counts || {};
      if (!(c[venueIko] > 0) && !(v.iko_chip_override || []).includes(venueIko)) return false;
    }
    return true;
  }), [searchedVenues, venueArea, venueIko]);

  const filterActive = qLower || (pill === "events" && (iko !== "all" || day !== "any" || budget !== "any" || area !== "any"))
                       || (pill === "venues" && (venueIko !== "all" || venueArea !== "any"));

  const activeChips = [];
  if (pill === "events") {
    if (iko !== "all") activeChips.push({ label: `iko: ${iko}`, clear: () => setIko("all") });
    if (day !== "any") activeChips.push({ label: `day: ${day === "pick" ? dayPick : day}`, clear: () => { setDay("any"); setDayPick(""); } });
    if (budget !== "any") activeChips.push({ label: `price: ${budget}`, clear: () => setBudget("any") });
    if (area !== "any") activeChips.push({ label: `area: ${area.toLowerCase()}`, clear: () => setArea("any") });
    if (loc !== "nairobi") activeChips.push({ label: `area: ${REGIONS.find((r) => r.k === loc)?.label || loc}`, clear: () => setLoc("nairobi") });
  }
  if (pill === "venues") {
    if (venueIko !== "all") activeChips.push({ label: `iko: ${venueIko}`, clear: () => setVenueIko("all") });
    if (venueArea !== "any") activeChips.push({ label: `area: ${venueArea.toLowerCase()}`, clear: () => setVenueArea("any") });
  }

  // Wave 15.12 (Vania 5 Oct) · scroll-edge fade. Anchored to the chip
  // row's immediate parent (position:relative) with `bottom: -22` so the
  // fade top sits flush with the chip row's bottom edge (0px gap) when
  // scrolled. Only rendered on desktop — phone Discover has no sticky
  // chip row (search+chips scroll with content).
  const fadeEl = (
    <div aria-hidden data-testid="feed-top-fade"
         data-visible={scrolled ? "1" : "0"}
         style={{ position: "absolute", left: 0, right: 0, bottom: -38,
                  height: 22, pointerEvents: "none", zIndex: 2,
                  opacity: scrolled ? 1 : 0,
                  transition: "opacity 180ms ease-out",
                  background: "var(--ww-ground-fade)" }} />
  );

  const counts = {
    events: filteredForRails.length,
    venues: filteredVenues.length,
    organisers: searchedOrgs.length,
    watu: people.length,
  };

  const chains = useChains();
  const rails = useMemo(() => {
    const now = new Date();
    // Fixed head buckets + open-ended month buckets.
    const head = { weekend: [], week: [], next_week: [] };
    const months = {};
    for (const e of filteredForRails) {
      const k = dayBand(nextStartAt(e), now);
      if (!k) continue;
      if (k in head) head[k].push(e);
      else if (k.startsWith("month:")) {
        (months[k] = months[k] || []).push(e);
      }
    }
    // WW-173 · a chain sits once, by date, in the rail of its next event (no chains rail).
    const all = [...Object.values(head), ...Object.values(months)];
    Object.values(chains).filter((c) => c.status !== "ended").forEach((c) => {
      const rail = all.find((r) => r.some((e) => e.id === c.next_event_id));
      if (!rail) return;
      const nxt = rail.find((e) => e.id === c.next_event_id);
      // WW-173.a · the chain's events leave every rail once its card is placed.
      all.forEach((r) => { for (let i = r.length - 1; i >= 0; i--) if (r[i].chain_id === c.id) r.splice(i, 1); });
      rail.push({ id: `chain-${c.id}`, __chain: c, start_at: nextStartAt(nxt) });
    });
    const sortByStart = (arr) => arr.sort((a, b) => (nextStartAt(a) || "").localeCompare(nextStartAt(b) || ""));
    Object.values(head).forEach(sortByStart);
    Object.values(months).forEach(sortByStart);
    const monthKeys = Object.keys(months).sort();
    return { head, months, monthKeys };
  }, [filteredForRails, chains]);

  return (
    <div className="flex h-full flex-col" data-testid="discover-page"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      {/* Wave 9 · desktop · 3-row filter stack (tabs | search+filter | iko chips).
          Phone keeps the original stacked search → tabs → chips layout. */}
      {isDesktop && (
        <div className="flex-shrink-0"
             style={{ background: "var(--ww-ground)",
                      borderBottom: "1px solid var(--ww-line)",
                      position: "relative",
                      paddingBottom: 20 }}>
          <div className="ww-container" style={{ paddingTop: 12, paddingBottom: 12 }}
               data-testid="discover-filter-row">
            <div className="ww-desktop-tab-row">
              <LovableTabs tabs={PILLS} active={pill} onChange={setPill} testId="discover-pill-bar" />
            </div>
            {/* WW-146.d · desktop search moved to the top bar · WW-146.e · iko chips + filters on one row */}
            <div className="mt-3 flex items-center gap-3" data-testid="discover-iko-row" style={{ position: "relative" }}>
              <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                {pill === "events" && <IkoChipRow active={iko} onSelect={setIko} keys={IKO_KEYS} />}
              </div>
              <button data-testid="filter-funnel" onClick={() => setSheetOpen(true)}
                      style={{ height: 36, padding: "0 14px", borderRadius: 999, background: "var(--ww-sunken)",
                               border: "1px solid var(--ww-line)", color: "var(--ww-text)", cursor: "pointer",
                               display: "inline-flex", alignItems: "center", gap: 6, position: "relative", flex: "0 0 auto",
                               fontFamily: "Poppins", fontWeight: 600, fontSize: 13 }}>
                <Filter size={14} /> filters
                {activeChips.length > 0 && (
                  <span style={{ position: "absolute", top: 3, right: 3, width: 8, height: 8, borderRadius: 999, background: "#FFD60A" }} />
                )}
              </button>
              {fadeEl}
            </div>
          </div>
        </div>
      )}
      <div ref={scrollRef} className="flex-1 overflow-y-auto pb-10"
           style={{ overscrollBehavior: "contain",
                    position: "relative" }}
           data-testid="discover-scroll-region">
        <div className="ww-container">
          {/* Phone-only search + funnel + tabs (desktop moved into the bar). */}
          {!isDesktop && (
          <div className="relative flex gap-2" data-testid="discover-search-wrap" style={{ marginTop: 14 }}>
            <div style={{ position: "relative", flex: 1 }}>
              <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "var(--ww-text-muted)" }}>
                <Search size={18} />
              </span>
              <input data-testid="discover-search-input-phone"
                     value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="search events, venues, people"
                     style={{ width: "100%", height: 52, padding: "0 16px 0 44px",
                              background: "var(--ww-sunken)", border: "1px solid #8A8AB0", borderRadius: 14,
                              color: "var(--ww-text)", fontFamily: "Arial", fontSize: 15, outline: "none" }} />
            </div>
            <button data-testid="filter-funnel-phone" onClick={() => setSheetOpen(true)} aria-label="filters"
                    style={{ width: 52, height: 52, borderRadius: 14, background: "var(--ww-sunken)",
                             border: "1px solid #8A8AB0", color: "var(--ww-text)", cursor: "pointer",
                             display: "grid", placeItems: "center", position: "relative" }}>
              <Filter size={18} />
              {activeChips.length > 0 && (
                <span style={{ position: "absolute", top: 6, right: 6, width: 10, height: 10, borderRadius: 999, background: "#FFD60A" }} />
              )}
            </button>
          </div>
          )}

          {/* Phone-only tab bar and iko row (desktop moved into the bar). */}
          {!isDesktop && (
          <div className="mt-4">
            <LovableTabs tabs={PILLS} active={pill} onChange={setPill} testId="discover-pill-bar-phone" />
          </div>
          )}

          {!isDesktop && pill === "events" && (
            <div className="mt-3" data-testid="discover-iko-row-phone">
              <IkoChipRow active={iko} onSelect={setIko} keys={IKO_KEYS} />
            </div>
          )}

          {/* Wave 7 · date-chip row removed. Day choice now lives in the filter sheet. */}

          {/* Active filter chip row */}
          {activeChips.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2" data-testid="active-filter-chips">
              {activeChips.map((c, i) => (
                <button key={i} onClick={c.clear}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6,
                                 padding: "6px 12px", borderRadius: 999,
                                 background: "var(--ww-surface)", border: "1px solid var(--ww-accent-ink)",
                                 color: "var(--ww-accent-ink)", fontFamily: "Arial", fontSize: 12, cursor: "pointer" }}>
                  {c.label} <X size={12} />
                </button>
              ))}
            </div>
          )}

          {/* Body */}
          {loading && <p className="py-12 text-center" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)" }}>loading…</p>}

          {!loading && pill === "events" && loc === "outoftown" && <OutsideNairobiList groups={outsideGroups} />}
          {!loading && pill === "events" && loc === "safari" && (safariEvents.length > 0 ? (
            <ResultsGrid rows={safariEvents} setViewer={setViewer} />
          ) : (
            <p className="py-12 text-center" data-testid="discover-safari-empty" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)" }}>
              nothing outside kenya yet
            </p>
          ))}
          {!loading && pill === "events" && loc === "nairobi" && (
            <>
              {filterActive ? (
                <ResultsGrid rows={filteredForRails} setViewer={setViewer} />
              ) : (
                <>
                  {rails.head.weekend.length > 0 && (
                    <Rail testId="rail-weekend" title="this weekend" items={rails.head.weekend} setViewer={setViewer} />
                  )}
                  {rails.head.week.length > 0 && (
                    <Rail testId="rail-week" title="this week" items={rails.head.week} setViewer={setViewer} />
                  )}
                  {rails.head.next_week.length > 0 && (
                    <Rail testId="rail-next-week" title="next week" items={rails.head.next_week} setViewer={setViewer} />
                  )}
                  {rails.monthKeys.map((k) => (
                    rails.months[k].length > 0 ? (
                      <Rail key={k} testId={`rail-${k.replace(":", "-")}`}
                            title={monthLabel(k)} items={rails.months[k]} setViewer={setViewer} />
                    ) : null
                  ))}
                </>
              )}
              {!filterActive
                && !rails.head.weekend.length && !rails.head.week.length && !rails.head.next_week.length
                && !rails.monthKeys.length && (
                <p className="py-12 text-center" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)" }}>
                  nothing upcoming yet. new drops soon.
                </p>
              )}
            </>
          )}

          {!loading && pill === "venues"     && <VenuesGrid venues={filteredVenues} />}
          {!loading && pill === "organisers" && <OrganisersGrid organisers={searchedOrgs} />}
          {!loading && pill === "watu"       && <WatuGrid members={people} />}
        </div>
      </div>

      {viewer && <PosterViewer viewer={viewer} onClose={() => setViewer(null)} />}
      {sheetOpen && (
        <FilterSheet onClose={() => setSheetOpen(false)} isDesktop={isDesktop}
          count={pill === "events" ? (loc === "nairobi" ? filteredForRails.length : loc === "outoftown" ? outsideGroups.reduce((n, g) => n + g.count, 0) : safariEvents.length) : null}
          onClear={() => { setIko("all"); setDay("any"); setDayPick(""); setBudget("any"); setArea("any"); setLoc("nairobi"); setVenueIko("all"); setVenueArea("any"); }}>
          {pill === "events" && (
            <EventFilterTabs isDesktop={isDesktop} panels={{
              iko: <IkoChipRow active={iko} onSelect={setIko} keys={IKO_KEYS} />,
              area: <AreaPanel region={loc} setRegion={setLoc} area={area} setArea={setArea} />,
              day: (
              <div className="flex flex-wrap gap-2">
                {[["any","any day"],["weekend","this weekend"],["week","this week"],["next_week","next week"],["today","today"],["tomorrow","tomorrow"],["pick","pick a date"]].map(([k, l]) => (
                  <SheetChip key={k} on={day === k} onClick={() => setDay(k)}>{l}</SheetChip>
                ))}
                {day === "pick" && (
                  <input type="date" value={dayPick} onChange={(e) => setDayPick(e.target.value)}
                         data-testid="discover-filter-date"
                         style={{ padding: "8px 12px", borderRadius: 999,
                                  background: "var(--ww-sunken)", border: "1px solid #2F2F52",
                                  color: "var(--ww-text)", fontFamily: "Arial", fontSize: 13 }} />
                )}
              </div>
              ),
              price: (
              <div className="flex flex-wrap gap-2">
                {BUDGETS.map((b) => <SheetChip key={b.key} on={budget === b.key} onClick={() => setBudget(b.key)}>{b.label}</SheetChip>)}
              </div>
              ),
            }} />
          )}
          {pill === "venues" && (
            <>
              <SheetH>area</SheetH>
              <div className="flex flex-wrap gap-2">
                <SheetChip on={venueArea === "any"} onClick={() => setVenueArea("any")}>any area</SheetChip>
                {AREAS.map((a) => <SheetChip key={a} on={venueArea === a} onClick={() => setVenueArea(a)}>{a.toLowerCase()}</SheetChip>)}
              </div>
              <SheetH>iko</SheetH>
              <IkoChipRow active={venueIko} onSelect={setVenueIko} keys={IKO_KEYS} />
            </>
          )}
          {(pill === "organisers" || pill === "watu") && (
            <p style={{ fontFamily: "Arial", color: "var(--ww-text-muted)", padding: "24px 4px" }}>
              use the search bar to filter this list.
            </p>
          )}
        </FilterSheet>
      )}
    </div>
  );
}

function SheetH({ children }) {
  return (
    <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em",
                fontSize: 11, color: "var(--ww-text-muted)", margin: "16px 0 8px" }}>{children}</p>
  );
}
function SheetChip({ on, onClick, children }) {
  return (
    <button data-testid={`sheet-chip-${(children + "").replace(/\s+/g, "-")}`} onClick={onClick}
            style={{ whiteSpace: "nowrap", padding: "8px 14px", borderRadius: 999,
                     background: on ? "var(--ww-surface)" : "transparent",
                     color: on ? "var(--ww-accent-ink)" : "var(--ww-text-muted)",
                     border: `1px solid ${on ? "var(--ww-accent-ink)" : "var(--ww-line)"}`,
                     fontFamily: "Arial", fontSize: 13, minHeight: 36,
                     textTransform: "lowercase", cursor: "pointer" }}>{children}</button>
  );
}

function EventFilterTabs({ panels, isDesktop }) {
  // WW-146.f · iko, area, day, price as tabs: across the top on phone, down the left on desktop.
  const [tab, setTab] = useState("iko");
  return (
    <div data-testid="filter-tabs" style={{ display: "flex", flexDirection: isDesktop ? "row" : "column", gap: 16 }}>
      <div role="tablist" style={{ display: "flex", flexDirection: isDesktop ? "column" : "row", gap: 6, flex: "0 0 auto", minWidth: isDesktop ? 120 : 0 }}>
        {FILTER_TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} data-testid={`filter-tab-${t}`} onClick={() => setTab(t)}
                  style={{ padding: "8px 14px", borderRadius: 999, minHeight: 36, cursor: "pointer", textAlign: "left",
                           background: tab === t ? "var(--ww-text)" : "transparent", color: tab === t ? "var(--ww-ground)" : "var(--ww-text)",
                           border: "1px solid var(--ww-line-strong)", fontFamily: "Poppins", fontWeight: 600, fontSize: 13 }}>{t}</button>
        ))}
      </div>
      <div data-testid={`filter-panel-${tab}`} style={{ flex: 1, minWidth: 0 }}>{panels[tab]}</div>
    </div>
  );
}

function AreaPanel({ region, setRegion, area, setArea }) {
  // WW-146.g · three places as radio rows, folded; neighbourhood chips only after around nairobi is tapped.
  const [open, setOpen] = useState(false);
  return (
    <div role="radiogroup" data-testid="filter-area-regions">
      {REGIONS.map((r) => (
        <div key={r.k}>
          <button role="radio" aria-checked={region === r.k} data-testid={`filter-area-${r.k}`}
                  onClick={() => { setRegion(r.k); setOpen(r.k === "nairobi"); if (r.k !== "nairobi") setArea("any"); }}
                  style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 4px", minHeight: 44,
                           background: "transparent", border: 0, borderBottom: "1px solid var(--ww-line)", color: "var(--ww-text)",
                           cursor: "pointer", textAlign: "left" }}>
            <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: 999, border: "2px solid var(--ww-text)", flex: "0 0 auto",
                                             display: "grid", placeItems: "center" }}>
              {region === r.k && <span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--ww-text)" }} />}
            </span>
            <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14 }}>{r.label}</span>
            <span style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)" }}>{r.line}</span>
          </button>
          {r.k === "nairobi" && open && region === "nairobi" && (
            <div className="flex flex-wrap gap-2 py-3 pl-7" data-testid="filter-area-hoods">
              <SheetChip on={area === "any"} onClick={() => setArea("any")}>anywhere</SheetChip>
              {[...HOODS, ...TOWNS].map((a) => <SheetChip key={a} on={area === a} onClick={() => setArea(a)}>{a.toLowerCase()}</SheetChip>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function FilterSheet({ children, onClose, isDesktop, count, onClear }) {
  // WW-194.1 · below 1024 the sheet sits above the bottom nav so apply is always tappable.
  const navGap = typeof window !== "undefined" && window.innerWidth < 1024 ? "calc(76px + env(safe-area-inset-bottom))" : "0px";
  return (
    <div data-testid="filter-sheet"
         style={{ position: "fixed", inset: 0, background: "rgba(18,18,42,0.72)", zIndex: 80,
                  display: "flex", alignItems: "flex-end", justifyContent: "center" }}
         onClick={(e) => { if (e.currentTarget === e.target) onClose(); }}>
      <div style={{ background: "var(--ww-ground)", border: "1px solid #2F2F52",
                    borderRadius: "20px 20px 0 0", padding: 20, width: "100%", maxWidth: 720,
                    marginBottom: navGap, maxHeight: `calc(80vh - ${navGap})`, overflowY: "auto", color: "var(--ww-text)" }}>
        <div className="flex items-center justify-between mb-2">
          <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 20, textTransform: "lowercase" }}>filters</p>
          <button onClick={onClose} data-testid="filter-sheet-close" aria-label="close"
                  style={{ background: "transparent", color: "var(--ww-text)", border: 0, cursor: "pointer" }}>
            <X size={22} />
          </button>
        </div>
        {children}
        <div className="mt-6 flex items-center justify-between gap-3" data-testid="filter-sheet-footer"
             style={{ position: "sticky", bottom: -20, background: "var(--ww-ground)", padding: "12px 0 4px", zIndex: 1 }}>
          <button onClick={onClear} data-testid="filter-sheet-clear"
                  style={{ background: "transparent", border: 0, color: "var(--ww-text)", textDecoration: "underline",
                           fontFamily: "Arial", fontSize: 13, cursor: "pointer", minHeight: 40 }}>clear all</button>
          <button onClick={onClose} data-testid="filter-sheet-apply"
                  style={{ padding: "12px 20px", borderRadius: 999, background: "#FFD60A",
                           color: "#1A1A30", border: 0, fontFamily: "Poppins", fontWeight: 700,
                           textTransform: "lowercase", cursor: "pointer" }}>
            {count === null || count === undefined ? "apply" : `show ${count} ${count === 1 ? "event" : "events"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Rail({ title, items, setViewer, testId }) {
  // Wave 9 · desktop (≥1024) renders a wrapping grid; phone keeps the
  // horizontal rail. One shape less to maintain and no cut-off cards.
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1024;
  return (
    <section className="mt-6" data-testid={testId}>
      <h2 className="mb-3 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em", fontSize: 24, lineHeight: 1.05 }}>
        {title}
      </h2>
      {isDesktop ? (
        <div className="ww-grid-cards">
          {items.map((e) => e.__chain ? <DiscoverChainCard key={e.id} chain={e.__chain} /> : <VerticalCard key={e.id} event={e} onOpenPoster={setViewer} />)}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-3" style={{ scrollbarWidth: "none" }}>
          {items.map((e) => e.__chain ? <DiscoverChainCard key={e.id} chain={e.__chain} /> : <MiniCard key={e.id} event={e} onOpenPoster={setViewer} />)}
        </div>
      )}
    </section>
  );
}
function ResultsGrid({ rows, setViewer }) {
  return (
    <section className="mt-6" data-testid="discover-results-grid">
      {rows.length === 0 ? (
        <p className="py-12 text-center" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)" }}>nothing matches those filters.</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
          {rows.map((e) => <MiniCard key={e.id} event={e} onOpenPoster={setViewer} full />)}
        </div>
      )}
    </section>
  );
}
function MiniCard({ event: ev, onOpenPoster, full }) {
  const [failed, setFailed] = useState(false);
  const iko = ikoTextOnNight(ev.iko);
  const fill = IKO[ev.iko]?.bright || "var(--ww-line)";
  const openPoster = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (ev.cover_image_url) onOpenPoster({ src: ev.cover_image_url, alt: ev.title || "", returnEl: e.currentTarget });
  };
  return (
    <Link to={`/app/event/${ev.id}`} data-testid={`rail-item-${ev.id}`}
          style={{ flex: full ? "auto" : "0 0 auto", width: full ? "auto" : 160,
                   background: "var(--ww-surface)", border: "1px solid #2F2F52",
                   borderRadius: 12, overflow: "hidden", textDecoration: "none", color: "inherit" }}>
      <div style={{ background: fill, height: 96, display: "grid", placeItems: "center" }}>
        {ev.cover_image_url && !failed ? (
          <span role="button" tabIndex={0} onClick={openPoster}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPoster(e); } }}
                  aria-label="open poster"
                  style={{ background: "transparent", padding: 0, border: 0, cursor: "zoom-in", display: "inline-block" }}>
            <img src={ev.cover_image_url} alt="" onError={() => setFailed(true)} style={{ width: 60, height: 80, objectFit: "contain", borderRadius: 4 }} />
          </span>
        ) : <div style={{ width: 60, height: 80, background: "rgba(26,26,48,0.16)", borderRadius: 4 }} />}
      </div>
      <div style={{ padding: 10 }}>
        <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em", fontSize: 10, color: ikoTitleVar(ev.iko) }}>
          {ikoLabel(ev.iko).toLowerCase()}
        </p>
        <p style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em",
                     fontSize: 13, lineHeight: 1.1, color: "var(--ww-text)", margin: "3px 0",
                     textTransform: "lowercase",
                     display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {(ev.title || "").toLowerCase()}
        </p>
        <p style={{ fontFamily: "Arial", fontSize: 11, color: "var(--ww-text-muted)",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {metaLine(nextStartAt(ev), ev.venue_display)}
        </p>
      </div>
    </Link>
  );
}

function VenuesGrid({ venues }) {
  if (!venues || venues.length === 0)
    return <p className="py-12 text-center" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)" }}>no venues match.</p>;
  return (
    <section className="mt-6" data-testid="venues-grid"
             style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
      {venues.map((v) => (
        <Link key={v.id} to={`/app/venue/${v.id}`} data-testid={`venue-card-${v.id}`}
              style={{ background: "var(--ww-surface)", border: "1px solid #2F2F52",
                       borderRadius: 12, overflow: "hidden", textDecoration: "none", color: "inherit",
                       display: "flex", flexDirection: "column", minHeight: 148 }}>
          <div style={{ height: 60, background: "var(--ww-line)", display: "grid", placeItems: "center", color: "var(--ww-text-muted)" }}>
            <MapPin size={22} />
          </div>
          <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 6 }}>
            <p style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em",
                         fontSize: 15, textTransform: "lowercase", color: "var(--ww-text)" }}>{(v.name || "").toLowerCase()}</p>
            {v.area && <p style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)" }}>{v.area.toLowerCase()}</p>}
            <p style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text)" }}>
              {(v.upcoming_events_count || 0)} upcoming
            </p>
            <IkoMix counts={v.iko_mix?.counts || {}} total={v.iko_mix?.total || 0}
                    overrideChips={v.iko_chip_override} label="usually hosts" compact
                    testId={`venue-mix-${v.id}`} />
            {v.score_count > 0 && (
              <RoseRow value={v.score_average} colourway="yellow" size={14} ground="night"
                       provisional={false} demo={false} showNumber={false}
                       testId={`venue-roses-${v.id}`} />
            )}
          </div>
        </Link>
      ))}
    </section>
  );
}

function OrganisersGrid({ organisers }) {
  if (!organisers || organisers.length === 0)
    return <p className="py-12 text-center" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)" }}>no curators match.</p>;
  return (
    <section className="mt-6" data-testid="organisers-grid"
             style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
      {organisers.map((o) => <OrganiserCard key={o.handle} o={o} />)}
    </section>
  );
}

function OrganiserCard({ o }) {
  const { user } = useAuth();
  const isPaid = !!user && ["trialing", "founding", "paid", "comped"].includes(user.membership_state);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const isKibanda = ["trial", "active"].includes(o.kibanda_display_state || "");
  useEffect(() => {
    if (!user) return;
    client.get(`/follow/status?kind=organiser&key=${encodeURIComponent(o.handle)}`, { withCredentials: true })
      .then((r) => setFollowing(!!r.data.following)).catch(() => {});
  }, [user, o.handle]);
  const toggle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { window.location.href = "/admin/login?next=/app/discover"; return; }
    if (!isPaid) { window.location.href = "/app/membership"; return; }
    setBusy(true);
    try {
      if (following) {
        await client.delete(`/follow?kind=organiser&key=${encodeURIComponent(o.handle)}`, { withCredentials: true });
        setFollowing(false);
      } else {
        await client.post("/follow", { kind: "organiser", key: o.handle }, { withCredentials: true });
        setFollowing(true);
      }
    } catch {} finally { setBusy(false); }
  };
  return (
    <Link to={`/app/organiser/${o.handle}`} data-testid={`organiser-card-${o.handle}`}
          style={{ background: "var(--ww-surface)", border: "1px solid #2F2F52",
                   borderRadius: 12, overflow: "hidden", textDecoration: "none", color: "inherit",
                   display: "flex", flexDirection: "column", gap: 8, padding: 12 }}>
      <div className="flex items-center gap-3">
        {o.logo_url ? (
          <img src={o.logo_url} alt="" style={{ width: 40, height: 40, borderRadius: 999, objectFit: "cover",
                                                border: "1px solid #2F2F52" }} />
        ) : (
          // Msg 234 fix 2 · neutral avatar (dark surface, cream initial, hairline border).
          <div style={{ width: 40, height: 40, borderRadius: 999, background: "var(--ww-surface)", color: "var(--ww-text)",
                        border: "1px solid #2F2F52",
                        display: "grid", placeItems: "center", fontFamily: "Poppins", fontWeight: 800 }}>
            {(o.name || o.handle).slice(0,1).toUpperCase()}
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em",
                       fontSize: 14, textTransform: "lowercase", color: "var(--ww-text)",
                       whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {(o.name || o.handle).toLowerCase()}
            {isKibanda && (
              <span data-testid={`kibanda-mark-${o.handle}`}
                    aria-label="kibanda curator"
                    title="kibanda"
                    style={{
                      marginLeft: 6, display: "inline-block", width: 14, height: 14,
                      borderRadius: 3, background: "transparent",
                      border: "1.5px solid #C9C9DA", color: "var(--ww-text-muted)",
                      fontFamily: "Poppins", fontWeight: 800, fontSize: 9,
                      lineHeight: "11px", textAlign: "center", verticalAlign: "middle",
                    }}>k</span>
            )}
          </p>
          <p style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            @{o.handle}
            {/* Msg 234 fix 2 · muted outlined label pill (not Kili Yellow). */}
            <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 999,
                           background: "transparent", color: "var(--ww-text-muted)",
                           border: "1px solid #2F2F52",
                           fontFamily: "Arial", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em" }}>curator</span>
          </p>
        </div>
      </div>
      <p style={{ fontFamily: "Arial", fontSize: 11, color: "var(--ww-text-muted)" }}>
        {(o.upcoming_events_count || 0)} upcoming
      </p>
      <IkoMix counts={o.iko_mix?.counts || {}} total={o.iko_mix?.total || 0}
              overrideChips={o.iko_chip_override} label="usually runs" compact
              testId={`org-mix-${o.handle}`} />
      {o.score_count > 0 && (
        <RoseRow value={o.score_average} colourway="yellow" size={14} ground="night"
                 provisional={false} demo={false} showNumber={false}
                 testId={`org-roses-${o.handle}`} />
      )}
      <button data-testid={`follow-btn-${o.handle}`} onClick={toggle} disabled={busy}
              style={{ marginTop: "auto", padding: "8px 12px", borderRadius: 999,
                       background: following ? "transparent" : "#FFD60A",
                       color: following ? "var(--ww-accent-ink)" : "#1A1A30",
                       border: `1px solid var(--ww-accent-ink)`, cursor: "pointer",
                       fontFamily: "Poppins", fontWeight: 700, fontSize: 13, textTransform: "lowercase" }}>
        {!user ? "get in to follow" : !isPaid ? "become an insider to follow" : (following ? "following" : "follow")}
      </button>
    </Link>
  );
}

function WatuGrid({ members }) {
  if (!members || members.length === 0)
    return <p className="py-12 text-center" style={{ fontFamily: "Arial", color: "var(--ww-text-muted)" }}>no watu to show yet.</p>;
  return (
    <section className="mt-6" data-testid="watu-grid"
             style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
      {members.map((m) => <WatuCard key={m.user_id} m={m} />)}
    </section>
  );
}

function WatuCard({ m }) {
  const { user } = useAuth();
  const isPaid = !!user && ["trialing", "founding", "paid", "comped"].includes(user.membership_state);
  const [following, setFollowing] = useState(false);
  const picUrl = m.picture ? (m.picture.startsWith("http") ? m.picture : `${API}${m.picture}`) : "";
  useEffect(() => {
    if (!user) return;
    client.get(`/follow/status?kind=user&key=${encodeURIComponent(m.user_id)}`, { withCredentials: true })
      .then((r) => setFollowing(!!r.data.following)).catch(() => {});
  }, [user, m.user_id]);
  const toggle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { window.location.href = "/admin/login?next=/app/discover"; return; }
    if (!isPaid) { window.location.href = "/app/membership"; return; }
    try {
      if (following) {
        await client.delete(`/follow?kind=user&key=${encodeURIComponent(m.user_id)}`, { withCredentials: true });
        setFollowing(false);
      } else {
        await client.post("/follow", { kind: "user", key: m.user_id }, { withCredentials: true });
        setFollowing(true);
      }
    } catch {}
  };
  return (
    <Link to={m.handle ? `/app/people/${m.handle}` : "#"} data-testid={`watu-card-${m.user_id}`}
          style={{ background: "var(--ww-surface)", border: "1px solid #2F2F52",
                   borderRadius: 12, overflow: "hidden", textDecoration: "none", color: "inherit",
                   display: "flex", flexDirection: "column", gap: 8, padding: 12 }}>
      <div className="flex items-center gap-3">
        {picUrl ? (
          <img src={picUrl} alt="" style={{ width: 40, height: 40, borderRadius: 999, objectFit: "cover" }} />
        ) : (
          <div style={{ width: 40, height: 40, borderRadius: 999, background: "#FFD60A", color: "#1A1A30",
                        display: "grid", placeItems: "center", fontFamily: "Poppins", fontWeight: 800 }}>
            {(m.display_name || m.handle || "?").slice(0,1).toUpperCase()}
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em",
                       fontSize: 14, textTransform: "lowercase", color: "var(--ww-text)",
                       whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {(m.display_name || m.handle || "").toLowerCase()}
          </p>
          <p style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            @{m.handle}
          </p>
        </div>
      </div>
      {m.bio && (
        <p data-testid={`watu-bio-${m.user_id}`}
           style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)", lineHeight: 1.4,
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                    overflow: "hidden" }}>
          {m.bio}
        </p>
      )}
      <button data-testid={`watu-follow-${m.user_id}`} onClick={toggle}
              style={{ marginTop: "auto", padding: "8px 12px", borderRadius: 999,
                       background: following ? "transparent" : "#FFD60A",
                       color: following ? "var(--ww-accent-ink)" : "#1A1A30",
                       border: `1px solid var(--ww-accent-ink)`, cursor: "pointer",
                       fontFamily: "Poppins", fontWeight: 700, fontSize: 13, textTransform: "lowercase" }}>
        {!user ? "get in to follow" : !isPaid ? "become an insider to follow" : (following ? "following" : "follow")}
      </button>
    </Link>
  );
}

// Msg 234 blocker 2 · outside-nairobi list, grouped by town, sorted by date.
// Far-from-nairobi towns show a small "plan a trip" line.
function OutsideNairobiList({ groups }) {
  if (!groups || groups.length === 0) {
    return (
      <div className="rounded-2xl border p-8 text-center"
           style={{ borderColor: "var(--ww-line)", background: "var(--ww-surface)" }}
           data-testid="outside-empty">
        <p style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 16, textTransform: "lowercase", color: "var(--ww-text)" }}>
          nothing outside nairobi yet
        </p>
        <p style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text-muted)", marginTop: 4 }}>
          watu wako will surface Nakuru, Naivasha, Mombasa and beyond as soon as they land in the queue.
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-6" data-testid="outside-groups">
      {groups.map((g) => (
        <section key={g.town} data-testid={`outside-group-${g.town.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <div className="flex items-baseline gap-3">
            <h3 className="lowercase"
                style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.02em",
                         fontSize: 20, lineHeight: 1.1, color: "var(--ww-text)" }}>
              {g.town.toLowerCase()}
            </h3>
            {g.far && (
              <span style={{ fontFamily: "Arial", fontSize: 11, textTransform: "uppercase",
                              letterSpacing: "0.2em", color: "var(--ww-accent-ink)" }}>
                plan a trip
              </span>
            )}
            <span style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)" }}>· {g.count}</span>
          </div>
          <div className="mt-3 grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
            {g.events.map((ev) => (
              <a key={ev.id} href={`/app/event/${ev.id}`}
                 data-testid={`outside-event-${ev.id}`}
                 style={{ display: "block", padding: 12, borderRadius: 12,
                          background: "var(--ww-surface)", border: "1px solid #2F2F52",
                          textDecoration: "none", color: "inherit" }}>
                <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em",
                             fontSize: 10, color: "var(--ww-text-muted)" }}>
                  {(ev.iko || "").toLowerCase() || "event"}
                </p>
                <p className="mt-1 lowercase"
                   style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 15,
                            color: "var(--ww-text)", lineHeight: 1.2,
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {(ev.title || "").toLowerCase()}
                </p>
                <p className="mt-1" style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)" }}>
                  {(ev.start_at || "").slice(0, 10)}
                </p>
                {ev.venue_display && (
                  <p style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text-muted)",
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {ev.venue_display}
                  </p>
                )}
              </a>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
